/**
 * Le coach — « qu'est-ce que j'aurais dû faire ? »
 *
 * Le § 12 le range parmi les idées « presque gratuites », et c'est exact : tout ce
 * qu'il dit est déjà dans le dépôt. La décision optimale est celle du taulier
 * (niveau 8), lue dans les tables calculées par `sim/` sur 30,2 millions de parties ;
 * la rareté d'un lancer se déduit exactement de `PROBA_MAIN`. Il ne manquait qu'à les
 * montrer — et c'est ce qui donne enfin une valeur visible au travail du simulateur.
 *
 * ## Deux règles, et elles ne sont pas cosmétiques
 *
 * 1. **Ne jamais mentir par approximation.** Un conseil qui annonce « une fois sur
 *    cinq » là où c'est une fois sur huit enseigne quelque chose de faux, et le
 *    joueur n'a aucun moyen de s'en apercevoir. Les chiffres viennent du dénombrement
 *    exact des 216 tirages.
 * 2. **Se taire quand il n'y a rien à décider.** Pas de dés en main, un rampeau (le
 *    jet y est unique et posé d'office), le tour d'un autre : le coach rend `null`.
 *    Un bouton qui répond toujours quelque chose finit par répondre n'importe quoi.
 *
 * ⚠️ Ce module ne recalcule **aucune** programmation dynamique. Ce serait une seconde
 * implémentation à prouver, et elle divergerait un jour du Python sans que rien ne le
 * signale. Il lit les mêmes tables que l'IA — le coach conseille donc exactement ce
 * que le meilleur adversaire du jeu jouerait à votre place.
 */
import { evaluerMain, MAINS, PROBA_MAIN, } from "./combinaisons.js";
import { lancersAutorises, terminee } from "./moteur.js";
import { profilDe } from "./niveaux.js";
import { valeursAGarder } from "./niveaux.js";
import { contexteDepuisPartie } from "./pilote.js";
/**
 * Le score qu'il faut dépasser, ou `null` si personne n'a encore posé.
 *
 * ⚠️ Ce n'est pas le même selon la phase, et c'est **l'erreur la plus chère du 421**
 * (§ 7 du rapport) : en charge le danger est d'être le plus faible, il suffit donc de
 * faire mieux que le moins bon déjà posé ; en décharge il faut être le plus FORT,
 * c'est donc le meilleur qu'on doit battre.
 */
export function seuilAViser(partie) {
    const poses = partie.manche?.poses;
    if (!poses)
        return null;
    const scores = Object.values(poses).map((p) => evaluerMain(p.main).score);
    if (scores.length === 0)
        return null;
    return partie.phase === "DECHARGE" ? Math.max(...scores) : Math.min(...scores);
}
/**
 * Probabilité qu'un lancer de trois dés neufs dépasse strictement ce score.
 *
 * Dénombrement exact sur les 216 tirages — `PROBA_MAIN` porte la probabilité de
 * chacune des 56 mains distinctes, permutations comprises.
 */
export function probaDeDepasser(seuil) {
    let total = 0;
    for (const main of MAINS) {
        const combinaison = evaluerMain(main);
        if (combinaison.score > seuil)
            total += PROBA_MAIN[combinaison.cle] ?? 0;
    }
    return total;
}
/**
 * « une fois sur 8 » — une fraction se lit mieux qu'un pourcentage à table.
 *
 * Rend `null` aux deux bouts : « une fois sur 1 » et « une fois sur l'infini » ne
 * veulent rien dire, et l'écran a de meilleures phrases pour « jamais » et « à tous
 * les coups ».
 */
export function denominateur(probabilite) {
    if (probabilite <= 0 || probabilite >= 0.995)
        return null;
    const sur = Math.round(1 / probabilite);
    return sur <= 1 ? null : sur;
}
/**
 * Ce que le taulier ferait à votre place, et pourquoi.
 *
 * `monSiege` restreint le conseil à VOTRE tour : sans lui, l'appareil conseille
 * quiconque a la main, ce qui est exactement ce qu'on veut en hot-seat, où le
 * téléphone sert tout le monde. Un adversaire virtuel, en revanche, n'est jamais
 * conseillé — il n'y a personne à qui parler.
 *
 * Rend `null` quand il n'y a rien à conseiller.
 */
export function conseiller(partie, tables, monSiege) {
    if (terminee(partie))
        return null;
    const manche = partie.manche;
    if (!manche || manche.tourDe === null)
        return null;
    if (monSiege !== undefined && manche.tourDe !== monSiege)
        return null;
    if (partie.joueurs[manche.tourDe]?.estIA)
        return null;
    // Dans un rampeau, le jet est unique et posé d'office : aucun choix à commenter.
    if (manche.rampeau)
        return null;
    const enCours = manche.enCours;
    if (!enCours)
        return null;
    const main = enCours.des.map((d) => d.valeur);
    const combinaison = evaluerMain(main);
    const seuil = seuilAViser(partie);
    const objectif = seuil === null ? null : (() => {
        const cle = MAINS.map(evaluerMain).find((c) => c.score === seuil)?.cle ?? String(seuil);
        const probabilite = probaDeDepasser(seuil);
        return { cle, probabilite, uneFoisSur: denominateur(probabilite) };
    })();
    const commun = { main: combinaison.cle, phase: partie.phase, objectif };
    // Plus de relance : le moteur n'accepte plus que de poser.
    if (enCours.lancers >= lancersAutorises(partie)) {
        return {
            ...commun,
            motif: "poser",
            garder: [...main].sort((a, b) => b - a),
            arreter: true,
            aRelancer: 0,
        };
    }
    const relances = (lancersAutorises(partie) - enCours.lancers >= 2 ? 2 : 1);
    // `alea = 1` : le taulier sans son taux d'erreur, c'est-à-dire la politique nue.
    const garder = valeursAGarder(profilDe(8), tables, main, relances, contexteDepuisPartie(partie), 1);
    if (garder.length === 3) {
        return { ...commun, motif: "arreter", garder, arreter: true, aRelancer: 0 };
    }
    return {
        ...commun,
        motif: garder.length === 0 ? "toutRelancer" : "garder",
        garder,
        arreter: false,
        aRelancer: 3 - garder.length,
    };
}
//# sourceMappingURL=coach.js.map